import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSave, faTimes, faChartLine, faUpload, faCalculator,
  faSpinner, faChevronLeft, faChevronRight, faCalendarAlt, faDollarSign, faUsers,
  faExchangeAlt, faChartBar, faFilter, faBroom, faFileImport, faDownload, faRefresh,
  faPalette, faRocket, faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import { BusinessMetric, UnitEconomicsMetric } from '../types/businessMetrics';
import businessMetricsService from '../services/businessMetricsService';
import { CostData } from '../types/cost';
import { TemplateDesignerTab } from './TemplateDesignerTab';
import { SmartImportWizard } from './SmartImportWizard';
import { Notification } from './Notification';

interface UnitEconomicsTabProps {
  data: CostData | null;
  loading?: boolean;
  customCosts?: number;
}

export const UnitEconomicsTab: React.FC<UnitEconomicsTabProps> = ({ data, loading = false, customCosts = 0 }) => {
  const [view, setView] = useState<'metrics' | 'designer'>('metrics');
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [metrics, setMetrics] = useState<BusinessMetric[]>([]);
  const [allMetrics, setAllMetrics] = useState<BusinessMetric[]>([]);
  const [unitEconomics, setUnitEconomics] = useState<UnitEconomicsMetric[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [calculatingUE, setCalculatingUE] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMetric, setEditingMetric] = useState<BusinessMetric | null>(null);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showImportMode, setShowImportMode] = useState(false);
  const [importMode, setImportMode] = useState<'create' | 'update' | 'merge'>('create');
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [csvSources, setCsvSources] = useState<Array<{ csvSource: string; csvFileName: string; count: number; importTimestamp: string }>>([]);
  const [showCsvList, setShowCsvList] = useState(false);

  // Notification
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message?: string;
  } | null>(null);

  // Advanced Filters
  const [filterSource, setFilterSource] = useState<'all' | 'manual' | 'csv_import'>('all');
  const [filterMetricType, setFilterMetricType] = useState<string>('all');
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  const fetchMetrics = useCallback(async () => {
    try {
      setLoadingMetrics(true);
      console.log('🔍 Fetching metrics for month:', selectedMonth);
      const fetchedMetrics = await businessMetricsService.getMetrics({ month: selectedMonth });
      console.log('📦 Fetched metrics:', {
        count: fetchedMetrics.length,
        bySource: {
          manual: fetchedMetrics.filter(m => m.source === 'manual').length,
          csv_import: fetchedMetrics.filter(m => m.source === 'csv_import').length
        },
        sampleMetrics: fetchedMetrics.slice(0, 3).map(m => ({
          name: m.metricName,
          value: m.value,
          month: m.month,
          source: m.source
        }))
      });
      setAllMetrics(fetchedMetrics);
      applyFilters(fetchedMetrics);
    } catch (error) {
      console.error('❌ Error fetching business metrics:', error);
    } finally {
      setLoadingMetrics(false);
    }
  }, [selectedMonth]);

  const applyFilters = (metricsToFilter: BusinessMetric[]) => {
    let filtered = [...metricsToFilter];

    // Source filter
    if (filterSource !== 'all') {
      filtered = filtered.filter(m => m.source === filterSource);
    }

    // Metric type filter
    if (filterMetricType !== 'all') {
      filtered = filtered.filter(m => m.metricType === filterMetricType);
    }

    // Date range filter
    if (dateRangeStart) {
      filtered = filtered.filter(m => m.dateRange.start >= dateRangeStart);
    }
    if (dateRangeEnd) {
      filtered = filtered.filter(m => m.dateRange.end <= dateRangeEnd);
    }

    setMetrics(filtered);
  };

  useEffect(() => {
    applyFilters(allMetrics);
  }, [filterSource, filterMetricType, dateRangeStart, dateRangeEnd, allMetrics]);

  const calculateUnitEconomics = useCallback(async () => {
    try {
      setCalculatingUE(true);
      const cloudCosts = (data?.summary?.totalSpend || 0);
      const totalCustomCosts = customCosts || 0;
      const result = await businessMetricsService.calculateUnitEconomics(selectedMonth, cloudCosts, totalCustomCosts);
      setUnitEconomics(result.metrics || []);
    } catch (error) {
      console.error('Error calculating unit economics:', error);
    } finally {
      setCalculatingUE(false);
    }
  }, [selectedMonth, data, customCosts]);

  const fetchAvailableMonths = useCallback(async () => {
    try {
      const months = await businessMetricsService.getAvailableMonths();
      setAvailableMonths(months);
    } catch (error) {
      console.error('Error fetching available months:', error);
    }
  }, []);

  const fetchCsvSources = useCallback(async () => {
    try {
      const sources = await businessMetricsService.getCsvSources(selectedMonth);
      setCsvSources(sources);
    } catch (error) {
      console.error('Error fetching CSV sources:', error);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchMetrics();
    fetchAvailableMonths();
    fetchCsvSources();
  }, [fetchMetrics, fetchAvailableMonths, fetchCsvSources]);

  useEffect(() => {
    if (metrics.length > 0) {
      calculateUnitEconomics();
    }
  }, [metrics, calculateUnitEconomics]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    if (direction === 'prev') {
      date.setMonth(date.getMonth() - 1);
    } else {
      date.setMonth(date.getMonth() + 1);
    }
    const newMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(newMonth);
  };

  const formatMonthDisplay = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getMetricIcon = (type: string) => {
    switch (type) {
      case 'customer_count': return faUsers;
      case 'revenue': return faDollarSign;
      case 'transactions': return faExchangeAlt;
      case 'arr':
      case 'mrr': return faChartLine;
      default: return faChartBar;
    }
  };

  const handleAddMetric = async (metricData: Omit<BusinessMetric, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      await businessMetricsService.createMetric(metricData);
      await fetchMetrics();
      setShowAddForm(false);

      setNotification({
        type: 'success',
        title: 'Metric Added',
        message: `${metricData.metricName} has been added successfully`
      });
    } catch (error: any) {
      console.error('Error adding metric:', error);
      setNotification({
        type: 'error',
        title: 'Add Failed',
        message: error.message || 'Failed to add metric'
      });
    }
  };

  const handleUpdateMetric = async (id: string, updateData: Partial<BusinessMetric>) => {
    try {
      await businessMetricsService.updateMetric(id, updateData);
      await fetchMetrics();
      setEditingMetric(null);
      setShowAddForm(false);

      setNotification({
        type: 'success',
        title: 'Metric Updated',
        message: `${updateData.metricName || 'Metric'} has been updated`
      });
    } catch (error: any) {
      console.error('Error updating metric:', error);
      setNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message || 'Failed to update metric'
      });
    }
  };

  const handleDeleteMetric = async (id: string) => {
    if (!window.confirm('Delete this metric?')) return;
    try {
      await businessMetricsService.deleteMetric(id);
      await fetchMetrics();

      setNotification({
        type: 'success',
        title: 'Metric Deleted',
        message: 'The metric has been deleted successfully'
      });
    } catch (error: any) {
      console.error('Error deleting metric:', error);
      setNotification({
        type: 'error',
        title: 'Delete Failed',
        message: error.message || 'Failed to delete metric'
      });
    }
  };

  const handleCSVImport = async (importedMetrics: BusinessMetric[]) => {
    try {
      console.log('🔵 CSV Import Started:', {
        metricsCount: importedMetrics.length,
        selectedMonth,
        sampleMetric: importedMetrics[0],
        importMode: showImportMode ? importMode : 'create'
      });

      let result;
      const csvName = importedMetrics[0]?.metadata?.csvFileName || 'CSV';

      if (showImportMode && importMode !== 'create') {
        result = await businessMetricsService.bulkImportWithMerge(importedMetrics, importMode);
        console.log('✅ Import result (merge):', result);

        setNotification({
          type: 'success',
          title: 'Import Successful!',
          message: `Imported: ${result.imported} | Updated: ${result.updated} metrics from ${csvName}`
        });
      } else {
        result = await businessMetricsService.bulkImportMetrics(importedMetrics);
        console.log('✅ Import result (create):', result);

        setNotification({
          type: 'success',
          title: 'Import Successful!',
          message: `Imported ${importedMetrics.length} metrics from ${csvName} to ${selectedMonth}`
        });
      }

      console.log('🔄 Refreshing metrics for month:', selectedMonth);

      // Auto-navigate to the month where data was imported
      const importedMonth = importedMetrics[0]?.month;
      if (importedMonth && importedMonth !== selectedMonth) {
        console.log(`📅 Auto-navigating from ${selectedMonth} to ${importedMonth} where data was imported`);
        setSelectedMonth(importedMonth);

        setNotification({
          type: 'info',
          title: 'Navigated to Import Month',
          message: `Data imported to ${formatMonthDisplay(importedMonth)}. Navigated automatically.`
        });
      }

      // Force immediate refresh
      setLoadingMetrics(true);
      await fetchMetrics();
      await fetchCsvSources();

      console.log('✅ Import complete. Current metrics count:', metrics.length);

      setShowCSVImport(false);
      setShowImportMode(false);
    } catch (error: any) {
      console.error('❌ Error importing metrics:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });

      setNotification({
        type: 'error',
        title: 'Import Failed',
        message: error.message || 'Unknown error occurred during import'
      });

      // Don't close wizard on error so user can see what went wrong
    }
  };

  const handleDeleteCsvSource = async (csvSource: string, csvFileName: string, count: number) => {
    if (!window.confirm(`Delete ${count} metrics from "${csvFileName}"?\n\nThis cannot be undone.`)) return;
    try {
      setLoadingMetrics(true);
      const result = await businessMetricsService.deleteByCsvSource(csvSource, selectedMonth);
      await fetchMetrics();
      await fetchCsvSources();

      setNotification({
        type: 'success',
        title: 'CSV Deleted',
        message: `Deleted ${result.deletedCount} metrics from ${csvFileName}`
      });
    } catch (error: any) {
      setNotification({
        type: 'error',
        title: 'Delete Failed',
        message: error.message
      });
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleClearImportedData = async () => {
    const importedCount = allMetrics.filter(m => m.source === 'csv_import').length;
    if (importedCount === 0) {
      setNotification({
        type: 'info',
        title: 'No Data to Clear',
        message: 'There is no imported data to delete.'
      });
      return;
    }
    const confirmed = window.confirm(`Delete all ${importedCount} imported metrics?\n\nThis cannot be undone.`);
    if (!confirmed) return;
    try {
      setLoadingMetrics(true);
      const result = await businessMetricsService.clearImportedData();
      await fetchMetrics();

      setNotification({
        type: 'success',
        title: 'Import Data Cleared',
        message: `Deleted ${result.deletedCount} imported metrics`
      });
    } catch (error: any) {
      setNotification({
        type: 'error',
        title: 'Clear Failed',
        message: error.message
      });
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleExport = () => {
    const filename = `business_metrics_${selectedMonth}_${filterSource}.csv`;
    businessMetricsService.downloadCSV(metrics, filename);
  };

  const clearFilters = () => {
    setFilterSource('all');
    setFilterMetricType('all');
    setDateRangeStart('');
    setDateRangeEnd('');
  };

  const cloudCosts = data?.summary?.totalSpend || 0;
  const totalCustomCosts = customCosts || 0;
  const totalCombinedCost = cloudCosts + totalCustomCosts;

  const uniqueMetricTypes = ['all', ...Array.from(new Set(allMetrics.map(m => m.metricType)))];

  if (view === 'designer') {
    return (
      <div className="space-y-2 font-mono">
        <div className="bg-white border border-gray-300 rounded p-2">
          <button
            onClick={() => setView('metrics')}
            className="px-3 py-1 text-xs text-blue-600 hover:text-blue-700 font-bold"
          >
            ← Back to Metrics
          </button>
        </div>
        <TemplateDesignerTab data={data} loading={loading} />
      </div>
    );
  }

  return (
    <div className="space-y-2 bg-gray-50 min-h-screen font-mono">
      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="bg-white border border-gray-300 rounded p-2">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faChartLine} className="text-blue-600" />
              <h1 className="text-sm font-bold">Unit Economics & Business Metrics</h1>
              <span className="text-xs px-2 py-0.5 rounded font-bold bg-blue-100 text-blue-700">
                {metrics.length} metrics
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-0.5">
              Track KPIs and calculate unit economics • ${totalCombinedCost.toLocaleString()} total cost
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchMetrics()}
              className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 font-bold"
            >
              <FontAwesomeIcon icon={faRefresh} className="mr-1" />
              Refresh
            </button>
            <button
              onClick={() => setView('designer')}
              className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 font-bold"
            >
              <FontAwesomeIcon icon={faPalette} className="mr-1" />
              Designer
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="bg-white border border-gray-300 rounded p-2">
          <div className="text-xs text-gray-600 font-bold">Cloud Costs</div>
          <div className="text-lg font-bold">${cloudCosts.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-gray-300 rounded p-2">
          <div className="text-xs text-gray-600 font-bold">Custom Costs</div>
          <div className="text-lg font-bold">${totalCustomCosts.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-blue-300 rounded p-2">
          <div className="text-xs text-gray-600 font-bold">Total Cost</div>
          <div className="text-lg font-bold text-blue-700">${totalCombinedCost.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-gray-300 rounded p-2">
          <div className="text-xs text-gray-600 font-bold">Manual</div>
          <div className="text-lg font-bold">{allMetrics.filter(m => m.source === 'manual').length}</div>
        </div>
        <div className="bg-white border border-green-300 rounded p-2">
          <div className="text-xs text-gray-600 font-bold">Imported</div>
          <div className="text-lg font-bold text-green-700">{allMetrics.filter(m => m.source === 'csv_import').length}</div>
        </div>
      </div>

      {/* Month & Filters */}
      <div className="bg-white border border-gray-300 rounded p-2">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => navigateMonth('prev')}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600" />
            <span className="text-sm font-bold">{formatMonthDisplay(selectedMonth)}</span>
          </div>
          <button
            onClick={() => navigateMonth('next')}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        {/* Advanced Filters */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-200 flex-wrap">
          <FontAwesomeIcon icon={faFilter} className="text-gray-400" />

          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value as any)}
            className="px-2 py-1 text-xs border border-gray-300 rounded font-bold"
          >
            <option value="all">All Sources</option>
            <option value="manual">Manual</option>
            <option value="csv_import">Imported</option>
          </select>

          <select
            value={filterMetricType}
            onChange={(e) => setFilterMetricType(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 rounded font-bold"
          >
            {uniqueMetricTypes.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type.replace('_', ' ')}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={dateRangeStart}
            onChange={(e) => setDateRangeStart(e.target.value)}
            placeholder="From"
            className="px-2 py-1 text-xs border border-gray-300 rounded font-bold"
          />

          <input
            type="date"
            value={dateRangeEnd}
            onChange={(e) => setDateRangeEnd(e.target.value)}
            placeholder="To"
            className="px-2 py-1 text-xs border border-gray-300 rounded font-bold"
          />

          {(filterSource !== 'all' || filterMetricType !== 'all' || dateRangeStart || dateRangeEnd) && (
            <button
              onClick={clearFilters}
              className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 border border-red-300 rounded font-bold"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-1" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        {csvSources.length > 0 && (
          <button
            onClick={() => setShowCsvList(!showCsvList)}
            className="px-3 py-1 text-xs text-purple-600 hover:bg-purple-50 border border-purple-300 rounded font-bold"
          >
            <FontAwesomeIcon icon={faFileImport} className="mr-1" />
            Manage CSVs ({csvSources.length})
          </button>
        )}
        {allMetrics.filter(m => m.source === 'csv_import').length > 0 && (
          <button
            onClick={handleClearImportedData}
            className="px-3 py-1 text-xs text-red-600 hover:bg-red-50 border border-red-300 rounded font-bold"
          >
            <FontAwesomeIcon icon={faBroom} className="mr-1" />
            Clear All Imported
          </button>
        )}
        {metrics.length > 0 && (
          <button
            onClick={handleExport}
            className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 font-bold"
          >
            <FontAwesomeIcon icon={faDownload} className="mr-1" />
            Export CSV
          </button>
        )}
        <button
          onClick={() => {
            setShowImportMode(true);
            setShowCSVImport(true);
          }}
          className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 font-bold"
        >
          <FontAwesomeIcon icon={faUpload} className="mr-1" />
          Import CSV
        </button>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 font-bold"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-1" />
          Add Metric
        </button>
      </div>

      {/* Import Mode Selection */}
      {showImportMode && (
        <div className="bg-blue-50 border border-blue-200 rounded p-2">
          <div className="flex items-center gap-4">
            <span className="text-xs font-bold text-blue-900">Import Mode:</span>
            <label className="flex items-center gap-1 text-xs font-bold cursor-pointer">
              <input
                type="radio"
                value="create"
                checked={importMode === 'create'}
                onChange={(e) => setImportMode(e.target.value as any)}
              />
              Create New
            </label>
            <label className="flex items-center gap-1 text-xs font-bold cursor-pointer">
              <input
                type="radio"
                value="merge"
                checked={importMode === 'merge'}
                onChange={(e) => setImportMode(e.target.value as any)}
              />
              Merge/Update
            </label>
            <label className="flex items-center gap-1 text-xs font-bold cursor-pointer">
              <input
                type="radio"
                value="update"
                checked={importMode === 'update'}
                onChange={(e) => setImportMode(e.target.value as any)}
              />
              Update Only
            </label>
            <span className="text-xs text-blue-700 ml-auto">
              {importMode === 'create' && 'Creates new metrics (may create duplicates)'}
              {importMode === 'merge' && 'Updates existing or creates new metrics'}
              {importMode === 'update' && 'Only updates existing metrics (name+month match)'}
            </span>
          </div>
        </div>
      )}

      {/* CSV Sources List */}
      {showCsvList && csvSources.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-purple-900">
              <FontAwesomeIcon icon={faFileImport} className="mr-2" />
              Imported CSV Files for {formatMonthDisplay(selectedMonth)}
            </h3>
            <button
              onClick={() => setShowCsvList(false)}
              className="text-purple-600 hover:text-purple-800"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="space-y-2">
            {csvSources.map((csv, index) => (
              <div
                key={csv.csvSource}
                className="bg-white border border-purple-200 rounded p-2 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-purple-100 text-purple-700">
                      CSV {index + 1}
                    </span>
                    <span className="text-sm font-bold text-gray-900">{csv.csvFileName}</span>
                    <span className="text-xs text-gray-500">
                      {csv.count} metrics
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    Imported: {new Date(csv.importTimestamp).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteCsvSource(csv.csvSource, csv.csvFileName, csv.count)}
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 font-bold ml-2"
                  disabled={loadingMetrics}
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-1" />
                  Delete
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-purple-700">
            <strong>Note:</strong> Deleting a CSV will remove all {csvSources.reduce((sum, csv) => sum + csv.count, 0)} metrics from these imports.
          </div>
        </div>
      )}

      {/* Metrics Table */}
      {!showAddForm && (
        <div className="bg-white border border-gray-300 rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="text-left p-2 font-bold">Product/Row</th>
                <th className="text-left p-2 font-bold">CSV Column</th>
                <th className="text-left p-2 font-bold">Type</th>
                <th className="text-left p-2 font-bold">Value</th>
                <th className="text-left p-2 font-bold">Unit</th>
                <th className="text-left p-2 font-bold">Source</th>
                <th className="text-right p-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingMetrics ? (
                <tr>
                  <td colSpan={7} className="text-center p-8">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-blue-600" />
                  </td>
                </tr>
              ) : metrics.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center p-8 text-gray-500">
                    <FontAwesomeIcon icon={faChartBar} className="text-4xl mb-2 block mx-auto text-gray-300" />
                    <div className="font-bold">No metrics found</div>
                    <div className="text-xs mt-1">Add metrics or import from CSV</div>
                  </td>
                </tr>
              ) : (
                metrics.map((metric) => {
                  // Extract row identifier and column header for CSV imports
                  const isCSVImport = metric.source === 'csv_import' && metric.metricName.includes(' - ');
                  const rowIdentifier = isCSVImport ? metric.metricName.split(' - ')[0] : metric.metricName;
                  const csvColumn = isCSVImport ? metric.metricName.split(' - ').slice(1).join(' - ') : '-';

                  return (
                    <tr key={metric.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={getMetricIcon(metric.metricType)} className="text-blue-600" />
                          <span className="font-bold">{rowIdentifier}</span>
                        </div>
                      </td>
                      <td className="p-2">
                        {isCSVImport ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-bold">
                            {csvColumn}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="p-2 font-bold">{metric.metricType.replace('_', ' ')}</td>
                      <td className="p-2 font-bold">{metric.value.toLocaleString()}</td>
                      <td className="p-2 font-bold">{metric.unit}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                          metric.source === 'csv_import' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {metric.source === 'csv_import' ? 'IMPORTED' : 'MANUAL'}
                        </span>
                      </td>
                      <td className="p-2">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => {
                              setEditingMetric(metric);
                              setShowAddForm(true);
                            }}
                            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteMetric(metric.id)}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <MetricFormCompact
          metric={editingMetric || undefined}
          selectedMonth={selectedMonth}
          onSave={editingMetric ?
            (data) => handleUpdateMetric(editingMetric.id, data) :
            handleAddMetric
          }
          onCancel={() => {
            setShowAddForm(false);
            setEditingMetric(null);
          }}
        />
      )}

      {/* Smart Import Wizard - imports data into current selected month */}
      {showCSVImport && (
        <SmartImportWizard
          isOpen={showCSVImport}
          onClose={() => {
            setShowCSVImport(false);
            setShowImportMode(false);
          }}
          onImport={handleCSVImport}
          selectedMonth={selectedMonth}
        />
      )}
    </div>
  );
};

// Compact Metric Form Component
interface MetricFormProps {
  metric?: BusinessMetric;
  selectedMonth: string;
  onSave: (data: Omit<BusinessMetric, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}

const MetricFormCompact: React.FC<MetricFormProps> = ({ metric, selectedMonth, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    metricName: metric?.metricName || '',
    metricType: metric?.metricType || 'customer_count' as any,
    value: metric?.value || 0,
    unit: metric?.unit || 'count',
    month: metric?.month || selectedMonth,
    dateRange: {
      start: metric?.dateRange.start || '',
      end: metric?.dateRange.end || ''
    },
    source: metric?.source || 'manual' as any,
    metadata: {
      notes: metric?.metadata?.notes || ''
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="bg-white border border-gray-300 rounded p-3 font-mono">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold">{metric ? 'Edit Metric' : 'Add New Metric'}</h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Metric Name *</label>
            <input
              type="text"
              value={formData.metricName}
              onChange={(e) => setFormData({ ...formData, metricName: e.target.value })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-bold"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Type *</label>
            <select
              value={formData.metricType}
              onChange={(e) => setFormData({ ...formData, metricType: e.target.value as any })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-bold"
            >
              <option value="customer_count">Customer Count</option>
              <option value="revenue">Revenue</option>
              <option value="arr">ARR</option>
              <option value="mrr">MRR</option>
              <option value="transactions">Transactions</option>
              <option value="usage">Usage</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Value *</label>
            <input
              type="number"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-bold"
              required
              step="0.01"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Unit *</label>
            <input
              type="text"
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-bold"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Start Date *</label>
            <input
              type="date"
              value={formData.dateRange.start}
              onChange={(e) => setFormData({
                ...formData,
                dateRange: { ...formData.dateRange, start: e.target.value }
              })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-bold"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">End Date *</label>
            <input
              type="date"
              value={formData.dateRange.end}
              onChange={(e) => setFormData({
                ...formData,
                dateRange: { ...formData.dateRange, end: e.target.value }
              })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-bold"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1">Notes</label>
          <textarea
            value={formData.metadata.notes}
            onChange={(e) => setFormData({
              ...formData,
              metadata: { ...formData.metadata, notes: e.target.value }
            })}
            rows={2}
            className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-bold"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1 text-xs font-bold border border-gray-300 rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-3 py-1 text-xs font-bold text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            <FontAwesomeIcon icon={faSave} className="mr-1" />
            {metric ? 'Update' : 'Add'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UnitEconomicsTab;
